import { useTranslation } from 'react-i18next'
import { Cookie } from 'lucide-react'
import { PageSeo, HeroSimple, Breadcrumbs, Button } from '@/components/ui'
import { useCookieConsent } from '@/contexts/CookieConsentContext'
import type { RouteKey } from '@/i18n/routes'

export function PrivacyCookiePage() {
  const { t } = useTranslation('common')
  const { openPreferences } = useCookieConsent()

  const title = t('privacy.title')
  const description = t('privacy.description')

  const breadcrumbItems = [
    { label: t('routes.home'), to: 'home' as RouteKey },
    { label: title },
  ]

  const lastUpdate = '2026-08-29'

  return (
    <>
      <PageSeo title={title} description={description} />

      <main>
        <HeroSimple title={title} subtitle={description} variant="dark" />

        <Breadcrumbs items={breadcrumbItems} />

        <section className="bg-cream py-16 md:py-24">
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
            <article className="space-y-12 text-muted">
              {/* Privacy */}
              <div>
                <h2 className="font-display text-3xl font-semibold text-ink">
                  {t('privacy.privacyTitle')}
                </h2>
                <div className="mt-6 space-y-4 leading-relaxed">
                  <h3 className="font-display text-xl font-medium text-ink">
                    {t('privacy.dataController')}
                  </h3>
                  <p>{t('privacy.dataControllerText')}</p>

                  <h3 className="font-display text-xl font-medium text-ink">
                    {t('privacy.purposesTitle')}
                  </h3>
                  <p>{t('privacy.purposesText')}</p>

                  <h3 className="font-display text-xl font-medium text-ink">
                    {t('privacy.rightsTitle')}
                  </h3>
                  <p>{t('privacy.rightsText')}</p>
                </div>
              </div>

              <div className="h-px bg-ink/10" />

              {/* Cookies */}
              <div>
                <h2 className="font-display text-3xl font-semibold text-ink">
                  {t('privacy.cookieTitle')}
                </h2>
                <div className="mt-6 space-y-4 leading-relaxed">
                  <p>{t('privacy.cookieIntro')}</p>

                  <h3 className="font-display text-xl font-medium text-ink">
                    {t('privacy.cookieTypesTitle')}
                  </h3>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>{t('privacy.cookieNecessary')}</li>
                    <li>{t('privacy.cookieAnalytics')}</li>
                    <li>{t('privacy.cookieMarketing')}</li>
                  </ul>
                </div>
              </div>

              {/* Cookie preferences CTA */}
              <div className="rounded-xl bg-ink-deep border border-primary/30 p-6 md:p-8 text-center">
                <Cookie className="mx-auto h-8 w-8 text-primary mb-4" aria-hidden="true" />
                <h3 className="font-display text-xl font-semibold text-white">
                  {t('privacy.manageTitle')}
                </h3>
                <p className="mt-2 text-white/70 max-w-md mx-auto">
                  {t('privacy.manageText')}
                </p>
                <Button
                  variant="primary-dark"
                  onClick={openPreferences}
                  className="mt-6"
                >
                  {t('privacy.openPreferences')}
                </Button>
              </div>

              <p className="text-sm text-muted/70 text-center">
                {t('privacy.lastUpdate')}: {lastUpdate}
              </p>
            </article>
          </div>
        </section>
      </main>
    </>
  )
}
